import { useEffect, useState } from 'react';
import { Play, Square, CheckCircle2, MapPin, Gauge, Clock, Pencil, Package, Lock, Moon } from 'lucide-react';
import { StartSheet, StopSheet, CorrectionSheet } from './StartStopSheets';
import { mapsUrl } from './parts';
import { OverdueStopBanner } from './OverdueStopBanner';
import { useTranslation, useI18nStore } from '../../../lib/i18n';
const tt = (k, p) => useI18nStore.getState().t(`startStop.${k}`, p);

function useElapsed(startIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  if (!startIso) return null;
  const mins = Math.max(0, Math.floor((now - new Date(startIso).getTime()) / 60000));
  return tt('elapsed', { h: Math.floor(mins / 60), m: String(mins % 60).padStart(2, '0') });
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-white/15 px-3 py-2">
    <div className="flex items-center gap-1 text-[11px] opacity-80"><Icon className="h-3.5 w-3.5" />{label}</div>
    <div className="text-lg font-black tabular-nums">{value ?? '—'}</div>
  </div>
);

const fmtVal = (v) => (v === true ? tt('yes') : v === false ? tt('no') : v);
const answerList = (a, rec, phase) => {
  const ans = rec?.[phase]?.answers || {};
  return (a.start_stop?.custom_fields || []).filter((f) => f.ss_phase === phase && ans[f.key] != null).map((f) => [f.label, fmtVal(ans[f.key])]);
};

export default function StartStopPanel({ a, onChanged }) {
  const { t: tr } = useTranslation();
  const t = (k, p) => tr(`startStop.${k}`, p);
  const [open, setOpen] = useState(null);
  const rec = a.start_stop_record;
  const status = rec?.status || 'idle';
  const elapsed = useElapsed(status === 'started' ? rec.start?.submitted_at : null);
  const close = () => setOpen(null);
  const done = () => { setOpen(null); onChanged(); };

  const ss = a.start_stop || {};
  const win = ss.window_state || 'open';
  const hhmm = (iso) => (iso ? iso.slice(11, 16) : '');
  const nextDay = ss.closes_at && ss.opens_at && ss.closes_at.slice(0, 10) !== ss.opens_at.slice(0, 10);
  const startNums = (a.start_stop?.custom_fields || []).filter((f) => f.ss_phase === 'start' && f.type === 'number' && rec?.start?.answers?.[f.key] != null);
  if (!a.is_today) return null;

  return (
    <div className="space-y-2" data-testid={`start-stop-panel-${a.id}`}>
      {a.is_overnight_carry && (
        <div className="rounded-xl bg-indigo-50 text-indigo-800 text-xs font-semibold px-3 py-2 flex items-center gap-1.5" data-testid={`ss-overnight-${a.id}`}>
          <Moon className="h-4 w-4" />{t('overnight', { time: hhmm(ss.closes_at) })}
        </div>
      )}
      {status === 'idle' && win === 'before' && (
        <div className="w-full rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-500 p-5 flex items-center gap-4" data-testid={`ss-start-locked-${a.id}`}>
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white dark:bg-slate-700"><Lock className="h-6 w-6" /></span>
          <span>
            <span className="block text-xl font-black text-slate-700 dark:text-slate-200">{t('startOpensAt', { time: hhmm(ss.opens_at) })}</span>
            <span className="block text-sm">{a.route_code} · {t('stopDeadline', { time: hhmm(ss.closes_at) })}{nextDay ? ` ${t('nextDay')}` : ''}</span>
          </span>
        </div>
      )}
      {status !== 'stopped' && win === 'closed' && (
        <div className="rounded-2xl border-2 border-rose-200 bg-rose-50 text-rose-800 p-4 text-sm" data-testid={`ss-window-closed-${a.id}`}>
          <b>{t('windowClosed')}</b> ({hhmm(ss.closes_at)}). {status === 'started' ? t('closedManagerCompletes') : t('closedInformManager')}
        </div>
      )}
      {status === 'idle' && win === 'open' && (
        <button type="button" onClick={() => setOpen('start')} data-testid={`ss-start-btn-${a.id}`}
          className="group w-full rounded-2xl bg-emerald-600 hover:bg-emerald-700 active:scale-[0.99] transition-[background-color,transform] text-white p-5 flex items-center gap-4 shadow-xl shadow-emerald-600/30">
          <span className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white/20">
            <span className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
            <Play className="relative h-8 w-8 fill-white" />
          </span>
          <span className="text-left">
            <span className="block text-3xl font-black tracking-wide">START</span>
            <span className="block text-sm opacity-90">{t('startTour')} · {a.route_code}{ss.preset?.time ? ` · ${ss.preset.time}` : ''}{a.start_stop?.required ? ` · ${t('required')}` : ''}</span>
          </span>
        </button>
      )}

      {status === 'started' && win !== 'closed' && (
        <div className="space-y-2">
        <OverdueStopBanner startIso={rec.start?.submitted_at} onStop={() => setOpen('stop')} testId={`ss-overdue-${a.id}`} />
        <div className="rounded-2xl bg-slate-900 text-white p-4 space-y-3 shadow-xl" data-testid={`ss-running-${a.id}`}>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm font-semibold"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse" />{t('running')}</span>
            <span className="text-2xl font-black tabular-nums" data-testid={`ss-elapsed-${a.id}`}>{elapsed}</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Stat icon={Clock} label={t('startLabel')} value={rec.start?.entered_time} />
            {a.start_stop?.fields?.start_km?.show && <Stat icon={Gauge} label={t('startKmShort')} value={rec.start?.km ?? (rec.start?.km_photo ? t('photo') : null)} />}
            {startNums.slice(0, a.start_stop?.fields?.start_km?.show ? 1 : 2).map((f) => <Stat key={f.key} icon={Package} label={f.label} value={rec.start.answers[f.key]} />)}
          </div>
          {ss.closes_at && (ss.window_start || ss.window_end) && <p className="text-xs text-slate-300 flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{t('stopDeadline', { time: hhmm(ss.closes_at) })}{nextDay ? ` ${t('nextDay')}` : ''}</p>}
          {rec.start?.address && <a href={mapsUrl(rec.start.lat, rec.start.lng)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-sky-300 underline"><MapPin className="h-3.5 w-3.5" />{rec.start.address}</a>}
          <button type="button" onClick={() => setOpen('stop')} data-testid={`ss-stop-btn-${a.id}`}
            className="w-full h-16 rounded-xl bg-rose-600 hover:bg-rose-700 active:scale-[0.99] transition-[background-color,transform] text-2xl font-black flex items-center justify-center gap-3">
            <Square className="h-7 w-7 fill-white" />STOP
          </button>
        </div>
        </div>
      )}

      {status === 'stopped' && (
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 p-4 space-y-2" data-testid={`ss-finished-${a.id}`}>
          <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-200 font-bold"><CheckCircle2 className="h-5 w-5" />{t('finished')}</div>
          <p className="text-sm text-slate-700 dark:text-slate-200">
            {rec.start?.entered_time} → {rec.stop?.entered_time}
            {rec.stop?.km != null && rec.start?.km != null && <> · <b>{rec.stop.km - rec.start.km} km</b></>}
            {answerList(a, rec, 'stop').map(([l, v]) => <span key={l}> · {l} <b>{v}</b></span>)}
          </p>
          {rec.correction_used
            ? <p className="text-xs text-slate-500" data-testid={`ss-correction-used-${a.id}`}>{t('correctionUsed')}</p>
            : <button type="button" onClick={() => setOpen('correct')} className="inline-flex items-center gap-1 text-sm font-semibold text-amber-700" data-testid={`ss-correct-btn-${a.id}`}><Pencil className="h-4 w-4" />{t('correctLast')}</button>}
        </div>
      )}

      {open === 'start' && <StartSheet a={a} onClose={close} onDone={done} />}
      {open === 'stop' && <StopSheet a={a} rec={rec} onClose={close} onDone={done} />}
      {open === 'correct' && <CorrectionSheet a={a} rec={rec} onClose={close} onDone={done} />}
    </div>
  );
}
